/**
 * Tipos e regras do disparo de e-mail de um dashboard HTML, do lado da tela. A conferência de verdade é a do servidor
 * (MontadorEmail, ConfigDisparo, Recorrencia); aqui só o que evita ida e volta: o que falta preencher e a frase da recorrência.
 *
 * O e-mail é um HTML com o bloco portal-email (blocos, textos e filtros da tela, como no dashboard). A tela guarda o HTML, a
 * recorrência, os destinatários e o valor de cada filtro que o HTML declara.
 */

export type Frequencia = 'diaria' | 'semanal' | 'mensal'
export type TipoFiltro = 'valores' | 'relativo'

export interface FiltroDoDisparo { id: string; tipo: TipoFiltro; valores?: string[] | null; periodo?: string | null }
/** Um filtro do book: uma coluna do dicionário, com valores fixos ou um período que anda com o calendário. */
export interface FiltroDoBook { coluna: string; tipo: TipoFiltro; valores?: string[] | null; periodo?: string | null }

/**
 * Os parâmetros do book de fotos do e-mail. O book é gerado sozinho a cada envio, no recorte de cada destinatário: ninguém gera à mão.
 */
export interface ConfigBook {
  colunaFoto: string
  agruparPor?: string | null
  rotuloDoAgrupamento?: string | null
  fotosPorSlide: number
  legenda: string[]
  capaGeral: boolean
  capaPorGrupo: boolean
  titulo?: string | null
  limiteFotos: number
  medida?: string | null
  filtros: FiltroDoBook[]
}

export interface ConfigDisparo { filtros: FiltroDoDisparo[]; book?: ConfigBook | null }

export const FOTOS_POR_SLIDE = [1, 2, 4, 6]
export const LIMITE_DE_FOTOS = 300
export const MAXIMO_DA_LEGENDA = 3

export function bookVazio(): ConfigBook {
  return {
    colunaFoto: '', agruparPor: null, rotuloDoAgrupamento: null, fotosPorSlide: 4, legenda: [], capaGeral: true, capaPorGrupo: false,
    titulo: null, limiteFotos: LIMITE_DE_FOTOS, medida: null, filtros: []
  }
}

/** O que falta no book para o servidor aceitar. */
export function pendenciasDoBook(b: ConfigBook): string[] {
  const faltas: string[] = []
  if (!b.colunaFoto) faltas.push('Book: escolha a coluna do link da foto.')
  if (!FOTOS_POR_SLIDE.includes(b.fotosPorSlide)) faltas.push('Book: escolha 1, 2, 4 ou 6 fotos por slide.')
  if (!(b.limiteFotos >= 1 && b.limiteFotos <= LIMITE_DE_FOTOS)) faltas.push(`Book: o limite de fotos vai de 1 a ${LIMITE_DE_FOTOS}.`)
  for (const f of b.filtros) {
    if (!f.coluna) faltas.push('Book: escolha a coluna de cada filtro.')
    else if (f.tipo === 'relativo' && !f.periodo) faltas.push(`Book: escolha o período do filtro de ${f.coluna}.`)
    else if (f.tipo === 'valores' && !(f.valores?.length)) faltas.push(`Book: o filtro de ${f.coluna} está sem valores.`)
  }
  return faltas
}

export interface Destinatario { userId: number; nome: string; email?: string | null }

export interface DisparoEntrada {
  ambienteId: number
  nome: string
  assunto: string
  /** O HTML do e-mail inteiro, com o bloco portal-email. */
  layout: string
  config: ConfigDisparo
  ativo: boolean
  frequencia: Frequencia
  horario: string
  diasDaSemana: number[]
  diaDoMes: number | null
  destinatarios: number[]
}

export interface Disparo extends Omit<DisparoEntrada, 'destinatarios'> {
  id: number
  dashboardId: number
  ambienteNome: string
  proximoEnvioEm?: string | null
  ultimoEnvioEm?: string | null
  /** Há um envio rodando agora no servidor: a lista mostra "Processando" e trava o Testar. */
  emProcessamento?: boolean
  destinatarios: Destinatario[]
}

export interface Opcoes {
  relatorio: string
  ambientes: { id: number; nome: string }[]
  periodos: string[]
}

export interface Achado { nivel: string; mensagem: string }

/** O que o servidor lê do HTML: o que bloqueia, o que avisa, os filtros da tela e os blocos declarados. */
export interface Analise {
  achados: Achado[]
  filtros: { id: string; rotulo: string; coluna: string }[]
  blocos: string[]
  /** O HTML usa {{book.link}}, {{book.fotos}} ou data-se-book: o disparo precisa do book configurado. */
  usaBook?: boolean
}

export const DIAS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb']

export const PERIODOS: Record<string, string> = {
  hoje: 'Hoje',
  ontem: 'Ontem',
  ultimos_7_dias: 'Últimos 7 dias (até ontem)',
  ultimos_30_dias: 'Últimos 30 dias (até ontem)',
  semana_atual: 'Semana atual (de segunda até hoje)',
  semana_anterior: 'Semana anterior (segunda a domingo)',
  mes_atual: 'Mês atual (do dia 1º até hoje)',
  mes_anterior: 'Mês anterior'
}

export const FREQUENCIAS: Record<Frequencia, string> = { diaria: 'Todo dia', semanal: 'Toda semana', mensal: 'Todo mês' }

export function disparoVazio(ambienteId: number): DisparoEntrada {
  return {
    ambienteId, nome: '', assunto: '{{relatorio}} · {{data}}', layout: '', ativo: false,
    config: { filtros: [] },
    frequencia: 'diaria', horario: '08:00', diasDaSemana: [1, 2, 3, 4, 5], diaDoMes: 1, destinatarios: []
  }
}

/** A tela de edição trabalha com ids; o servidor devolve pessoas. */
export function paraEntrada(d: Disparo): DisparoEntrada {
  return {
    ambienteId: d.ambienteId, nome: d.nome, assunto: d.assunto, layout: d.layout, ativo: d.ativo,
    config: {
      filtros: (d.config?.filtros ?? []).map(f => ({ ...f })),
      book: d.config?.book ? { ...d.config.book, legenda: [...d.config.book.legenda], filtros: d.config.book.filtros.map(f => ({ ...f })) } : null
    },
    frequencia: d.frequencia, horario: d.horario, diasDaSemana: [...d.diasDaSemana], diaDoMes: d.diaDoMes ?? 1,
    destinatarios: d.destinatarios.map(x => x.userId)
  }
}

/** "Todo dia às 08:00", "Seg, Qua e Sex às 08:00", "Todo dia 5 às 08:00". */
export function descreverRecorrencia(d: Pick<DisparoEntrada, 'frequencia' | 'horario' | 'diasDaSemana' | 'diaDoMes'>): string {
  if (d.frequencia === 'semanal') {
    const nomes = [...d.diasDaSemana].sort((a, b) => a - b).map(x => DIAS[x]).filter(Boolean)
    const lista = nomes.length <= 1 ? nomes.join('') : `${nomes.slice(0, -1).join(', ')} e ${nomes[nomes.length - 1]}`
    return `${lista || 'Nenhum dia'} às ${d.horario}`
  }
  if (d.frequencia === 'mensal') return `Todo dia ${d.diaDoMes ?? '?'} às ${d.horario}`
  return `Todo dia às ${d.horario}`
}

/** O que falta para o servidor aceitar. Vazio quando está pronto para salvar. */
export function pendencias(d: DisparoEntrada, analise: Analise | null): string[] {
  const faltas: string[] = []
  if (!d.nome.trim()) faltas.push('Dê um nome ao disparo.')
  if (!d.assunto.trim()) faltas.push('Escreva o assunto.')
  if (!d.layout.trim()) faltas.push('Cole o HTML do e-mail.')
  else if (analise && analise.achados.some(a => a.nivel === 'bloqueio')) faltas.push('O HTML do e-mail tem bloqueios (veja acima).')
  if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(d.horario)) faltas.push('Informe o horário como HH:mm.')
  if (d.frequencia === 'semanal' && d.diasDaSemana.length === 0) faltas.push('Escolha ao menos um dia da semana.')
  if (d.frequencia === 'mensal' && !(d.diaDoMes && d.diaDoMes >= 1 && d.diaDoMes <= 31)) faltas.push('Escolha o dia do mês (1 a 31).')
  for (const f of d.config.filtros) {
    if (f.tipo === 'relativo' && !f.periodo) faltas.push(`Escolha o período do filtro "${f.id}".`)
    if (f.tipo === 'valores' && !(f.valores?.length)) faltas.push(`O filtro "${f.id}" está sem valores.`)
  }
  if (analise?.usaBook && !d.config.book) faltas.push('O HTML usa o book ({{book.link}}): preencha o book de fotos abaixo.')
  if (d.config.book) faltas.push(...pendenciasDoBook(d.config.book))
  if (d.ativo && d.destinatarios.length === 0) faltas.push('Escolha ao menos um destinatário para ativar.')
  return faltas
}

/** Tira do disparo os filtros que o HTML não declara mais (o servidor recusaria). Devolve o mesmo objeto se nada mudou. */
export function podarFiltros(config: ConfigDisparo, declarados: string[]): ConfigDisparo {
  const restantes = config.filtros.filter(f => declarados.includes(f.id))
  return restantes.length === config.filtros.length ? config : { ...config, filtros: restantes }
}

/** Um valor por linha (ou separado por ponto e vírgula), sem vazios nem repetidos. */
export function lerValores(texto: string): string[] {
  return Array.from(new Set(texto.split(/[\n;]/).map(x => x.trim()).filter(Boolean)))
}
